import React, { useState, useEffect, useRef } from "react";
import { photoFilenames } from "../constants/photoFilenames";
import { useNavigate } from "react-router-dom";
import { getAuth, signOut } from "firebase/auth";
import { doc, getDoc, collection, getDocs, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { tooltips } from "../constants/tooltips"; // Import centralized tooltips
import { getCurrentDate, getCurrentDayOfMonth, getTodayDay } from "../utils/currentDate";
import "../styles/App.css";
import AugustCountdownPage from "./AugustCountdownPage"; // Import the August countdown page
import { triviaQuestions } from "../constants/triviaQuestions";

const MILESTONES = [5, 10, 15, 20, 25, 30]; // Define your milestones here

// Define milestone colors for achieved milestones
const milestoneColors: { [key: number]: string } = {
  5: "#ffb3b3",      // light red
  10: "#ffd699",     // light orange
  15: "#b9fbc0",     // green
  20: "#b3e5fc",     // semi light blue
  25: "#d1b3ff",     // violet
  30: "#ffe066",     // gold
};

const milestoneHoverColors: { [key: number]: string } = {
  5: "#ff6f91",    // more vivid pink/red
  10: "#ffc043",   // deeper gold/orange
  15: "#5fd068",   // deeper green
  20: "#4fc3f7",   // brighter blue
  25: "#a084ee",   // deeper violet
  30: "#ffd600",   // strong gold
};

const milestoneTooltipKeys: { [key: number]: number } = {
  5: 31,
  10: 32,
  15: 33,
  20: 34,
  25: 35,
  30: 36,
};

const dayPageButtonStyle: React.CSSProperties = {
  background: "linear-gradient(90deg, #d16ba5 0%, #86a8e7 100%)",
  color: "#fff",
  border: "none",
  borderRadius: 16,
  padding: "10px 24px",
  fontWeight: 700,
  fontFamily: "'Quicksand', 'Fredoka', sans-serif",
  fontSize: 18,
  boxShadow: "0 2px 8px #d16ba522",
  cursor: "pointer",
  transition: "background 0.2s, transform 0.15s"
};

const NUM_BOUNCING = 10;
const IMAGE_SIZE = 200; // Slightly smaller
const VELOCITY = 0.5;   // Slower speed

// --- helper for random direction ---
function randomDirection() {
  const signX = Math.random() < 0.5 ? 1 : -1;
  const signY = Math.random() < 0.5 ? 1 : -1;
  const value = VELOCITY / Math.sqrt(2);
  return { dx: value * signX, dy: value * signY };
}

// --- helper for unique random indexes ---
function getRandomIndexes(arrLen: number, count: number) {
  const idxs = new Set<number>();
  while (idxs.size < count && idxs.size < arrLen) {
    idxs.add(Math.floor(Math.random() * arrLen));
  }
  return Array.from(idxs);
}

type Bouncer = {
  idx: number;
  fade: boolean;
};

const MainScreen: React.FC<{ isAugust: boolean | null; onReplayIntro?: () => void }> = ({ isAugust, onReplayIntro }) => {
  const navigate = useNavigate();
  const [currentDay, setCurrentDay] = useState(getTodayDay());
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [animatePart, setAnimatePart] = useState(""); // Tracks which part is animating
  const [lockedDay, setLockedDay] = useState<number | null>(null); // Tracks the locked day clicked
  const [role, setRole] = useState<string | null>(null); // Fetch role dynamically
  const [openedGifts, setOpenedGifts] = useState<{ [key: number]: boolean }>({}); // Tracks opened gifts
  const [totalCorrect, setTotalCorrect] = useState(0);
  const [milestones, setMilestones] = useState<number[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [triviaAnswers, setTriviaAnswers] = useState<any>({});
  const [milestoneTooltip, setMilestoneTooltip] = useState<{ text: string; x: number; y: number } | null>(null);
  const [bouncers, setBouncers] = useState<{ idx: number; fade: boolean }[]>(() =>
    getRandomIndexes(photoFilenames.length, NUM_BOUNCING).map(idx => ({ idx, fade: false }))
  );
  const [positions, setPositions] = useState<{ x: number; y: number; dx: number; dy: number }[]>([]);
  const animationRef = useRef<number | null>(null);
  const [showLogoTooltip, setShowLogoTooltip] = useState(false);
  const [showLogoMenu, setShowLogoMenu] = useState(false);
  const [showBouncingPhotos, setShowBouncingPhotos] = useState(() => {
    // Read from localStorage, default to true
    const stored = localStorage.getItem("showBouncingPhotos");
    return stored === null ? true : stored === "true";
  });

  // Persist switch state to localStorage
  useEffect(() => {
    localStorage.setItem("showBouncingPhotos", showBouncingPhotos ? "true" : "false");
  }, [showBouncingPhotos]);

  // Initialize positions after mount
  useEffect(() => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    // 10 evenly scattered positions (as percentages of the viewport)
    const percentPositions = [
      { x: 0.10, y: 0.15 },
      { x: 0.30, y: 0.10 },
      { x: 0.50, y: 0.18 },
      { x: 0.70, y: 0.12 },
      { x: 0.85, y: 0.22 },
      { x: 0.15, y: 0.65 },
      { x: 0.35, y: 0.80 },
      { x: 0.55, y: 0.75 },
      { x: 0.75, y: 0.85 },
      { x: 0.88, y: 0.65 },
    ];
    const positions = percentPositions.map(({ x, y }) => {
      const { dx, dy } = randomDirection();
      return {
        x: x * (w - IMAGE_SIZE),
        y: y * (h - IMAGE_SIZE),
        dx,
        dy,
      };
    });
    setPositions(positions);
  }, []);

  // Animate positions (start only after positions are initialized)
  useEffect(() => {
    if (positions.length !== NUM_BOUNCING) return;
    function animate() {
      const w = window.innerWidth;
      const h = window.innerHeight;
      setPositions(prev =>
        prev.map((pos) => {
          let { x, y, dx, dy } = pos;
          x += dx;
          y += dy;
          const maxX = w - IMAGE_SIZE;
          const maxY = h - IMAGE_SIZE;
          if (x < 0) { x = 0; dx = -dx; }
          if (x > maxX) { x = maxX; dx = -dx; }
          if (y < 0) { y = 0; dy = -dy; }
          if (y > maxY) { y = maxY; dy = -dy; }
          return { x, y, dx, dy };
        })
      );
      animationRef.current = requestAnimationFrame(animate);
    }
    animationRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [positions.length]); // Only start animation when positions are set

  const auth = getAuth();
  const user = auth.currentUser;

  useEffect(() => {
    // Fetch the user's role from Firestore
    const fetchRole = async () => {
      try {
        if (user) {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            setRole(userDoc.data().role);
          }
        } else {
          navigate("/"); // Redirect to login if no user is authenticated
        }
      } catch (error) {
        console.error("Error fetching role:", error);
      }
    };

    fetchRole();
  }, [navigate, user]);

  useEffect(() => {
    // Fetch the opened gift statuses from Firestore
    const fetchOpenedGifts = async () => {
      try {
        if (user) {
          const giftStatusCollection = collection(db, "users", user.uid, "giftStatus");
          const giftDocs = await getDocs(giftStatusCollection);

          const openedGiftsData: { [key: number]: boolean } = {};
          giftDocs.forEach((doc) => {
            const giftId = parseInt(doc.id.replace("gift", ""), 10); // Extract the day number from the document ID
            if (doc.data().isOpened) {
              openedGiftsData[giftId] = true;
            }
          });

          setOpenedGifts(openedGiftsData);
        }
      } catch (error) {
        console.error("Error fetching opened gifts:", error);
      }
    };

    fetchOpenedGifts();
  }, [user]);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = getCurrentDate();
      const nextDay = new Date(now);
      nextDay.setDate(getCurrentDayOfMonth() + 1);
      nextDay.setHours(0, 0, 0, 0);

      const diff = nextDay.getTime() - now.getTime();
      const newHours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const newMinutes = Math.floor((diff / (1000 * 60)) % 60);
      const newSeconds = Math.floor((diff / 1000) % 60);

      // Determine which part of the timer is changing
      if (newHours !== timeLeft.hours) {
        setAnimatePart("hours");
      } else if (newMinutes !== timeLeft.minutes) {
        setAnimatePart("minutes");
      } else if (newSeconds !== timeLeft.seconds) {
        setAnimatePart("seconds");
      }

      // Update all time parts together
      setTimeLeft({ hours: newHours, minutes: newMinutes, seconds: newSeconds });

      setTimeout(() => setAnimatePart(""), 500); // Reset animation after 0.5s
    }, 1000);

    return () => clearInterval(interval);
  }, [currentDay, timeLeft]);

  const handleDayClick = (day: number) => {
    if (role === "admin" || day <= getCurrentDayOfMonth()) {
      navigate(`/day/${day}`);
    } else {
      setLockedDay(day);
      setTimeout(() => setLockedDay(null), 2000);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/");
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  // Define the calendar rows
  const calendarRows = [
    [1, 2, 3, 4, 5, 6],
    [7, 8, 9, 10, 11, 12],
    [13, 14, 15, 16, 17, 18],
    [19, 20, 21, 22, 23, 24],
    [25, 26, 27, 28, 29],
    [30],
  ];

  useEffect(() => {
    const fetchProgress = async () => {
      if (user) {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          setTotalCorrect(userDoc.data().totalCorrect || 0);
          setMilestones(userDoc.data().milestones || []);
        }
      }
    };
    fetchProgress();
  }, [user]);

  useEffect(() => {
    const fetchTriviaAnswers = async () => {
      if (user) {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          setTriviaAnswers(userDoc.data().triviaAnswers || {});
        }
      }
    };
    fetchTriviaAnswers();
  }, [user]);

  // Ensure positions stay within bounds on resize
  useEffect(() => {
    function handleResize() {
      setPositions(prev =>
        prev.map(pos => ({
          ...pos,
          x: Math.min(pos.x, window.innerWidth - IMAGE_SIZE),
          y: Math.min(pos.y, window.innerHeight - IMAGE_SIZE),
        }))
      );
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // If it's August, show the August countdown page
  if (!isAugust) return <AugustCountdownPage />;

  // Otherwise, show the regular calendar UI
  return (
    <div className="main-screen" style={{ position: "relative", minHeight: "100vh" }}>
      {/* Lower the main UI by adding marginTop here */}
      <div style={{ marginTop: 170 }}>
        <button
          onClick={handleLogout}
          className="logout-button"
          style={dayPageButtonStyle}
          onMouseOver={e => (e.currentTarget.style.transform = "scale(1.07)")}
          onMouseOut={e => (e.currentTarget.style.transform = "scale(1)")}
        >
          Logout
        </button>

        {/* Speaker icons for SFX - now under the logo, fixed position */}
        <div
          style={{
            position: "fixed",
            top: 120,
            left: 19,
            zIndex: 10000,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 10,
          }}
        >
          <button
            className="speaker-btn"
            style={{
              background: "linear-gradient(135deg, #ffe9fa 0%, #b3e5fc 100%)",
              border: "none",
              borderRadius: "50%",
              width: 48,
              height: 48,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 8px #d16ba522",
              cursor: "pointer",
              transition: "transform 0.18s, box-shadow 0.18s"
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = "scale(1.13) rotate(-8deg)";
              e.currentTarget.style.boxShadow = "0 4px 16px #d16ba555";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = "scale(1) rotate(0deg)";
              e.currentTarget.style.boxShadow = "0 2px 8px #d16ba522";
            }}
            onClick={() => {
              const audio = new Audio("/sfx/audio1.mp3");
              audio.play();
            }}
            aria-label="Play sound effect 1"
          >
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
              <ellipse cx="14" cy="14" rx="14" ry="14" fill="#fff8fc"/>
              <path d="M8 18V10h4l5-4v16l-5-4H8z" fill="#d16ba5"/>
              <path d="M19 11a3 3 0 010 6" stroke="#d16ba5" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
          <button
            className="speaker-btn"
            style={{
              background: "linear-gradient(135deg, #ffe9fa 0%, #b3e5fc 100%)",
              border: "none",
              borderRadius: "50%",
              width: 48,
              height: 48,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 8px #d16ba522",
              cursor: "pointer",
              transition: "transform 0.18s, box-shadow 0.18s"
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = "scale(1.13) rotate(8deg)";
              e.currentTarget.style.boxShadow = "0 4px 16px #d16ba555";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = "scale(1) rotate(0deg)";
              e.currentTarget.style.boxShadow = "0 2px 8px #d16ba522";
            }}
            onClick={() => {
              const audio = new Audio("/sfx/audio2.mp3");
              audio.play();
            }}
            aria-label="Play sound effect 2"
          >
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
              <ellipse cx="14" cy="14" rx="14" ry="14" fill="#fff8fc"/>
              <path d="M20 18V10h-4l-5-4v16l5-4h4z" fill="#86a8e7"/>
              <path d="M9 11a3 3 0 000 6" stroke="#86a8e7" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Switch for toggling bouncing photos - now above the speaker buttons */}
        <div
          style={{
            position: "fixed",
            top: 80, // Position the switch above the speaker buttons, adjust as needed
            left: 20, // Controls horizontal position of the switch
            zIndex: 10001,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0,
          }}
        >
          <span style={{ position: "relative", display: "inline-block", width: 44, height: 26 }}>
            <input
              type="checkbox"
              checked={showBouncingPhotos}
              onChange={e => setShowBouncingPhotos(e.target.checked)}
              style={{
                opacity: 0,
                width: 44,
                height: 26,
                position: "absolute",
                left: 0,
                top: 0,
                cursor: "pointer",
                zIndex: 3, // Ensure the input is above the visual slider for full clickability
                margin: 0,
                padding: 0,
              }}
              aria-label="Toggle bouncing photos"
            />
            <span
              style={{
                position: "absolute",
                cursor: "pointer",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: showBouncingPhotos
                  ? "linear-gradient(90deg, #b9fbc0 0%, #d1b3ff 100%)"
                  : "#eee",
                borderRadius: 16,
                transition: "background 0.2s",
                boxShadow: showBouncingPhotos
                  ? "0 2px 8px #d16ba522"
                  : "0 1px 3px #bbb8",
                zIndex: 1,
                pointerEvents: "none", // Allow clicks to pass through to the input
              }}
            />
            <span
              style={{
                position: "absolute",
                left: showBouncingPhotos ? 22 : 2,
                top: 2,
                width: 22,
                height: 22,
                background: showBouncingPhotos ? "#d16ba5" : "#bbb",
                borderRadius: "50%",
                transition: "left 0.2s, background 0.2s",
                boxShadow: showBouncingPhotos
                  ? "0 2px 8px #d16ba522"
                  : "0 1px 3px #bbb8",
                zIndex: 2,
                pointerEvents: "none", // Thumb is visual only, not interactive
              }}
            />
          </span>
        </div>

        <h1 className="countdown-title">Countdown to Next Day:</h1>
        <div className="countdown">
          <span className={`countdown-part ${animatePart === "hours" ? "fadeIn" : ""}`}>
            {timeLeft.hours}h
          </span>
          <span className={`countdown-part ${animatePart === "minutes" ? "fadeIn" : ""}`}>
            {timeLeft.minutes}m
          </span>
          <span className={`countdown-part ${animatePart === "seconds" ? "fadeIn" : ""}`}>
            {timeLeft.seconds}s
          </span>
        </div>
        <div className="calendar">
          {calendarRows.map((row, rowIndex) => (
            <div key={rowIndex} className={`calendar-row ${rowIndex === 5 ? "last-row" : ""}`}>
              {row.map((day) => (
                <div
                  key={day}
                  className={`day ${role === "user" && day > currentDay ? "locked" : ""}`}
                  onClick={() => handleDayClick(day)}
                  data-tooltip={
                    lockedDay === day
                      ? "Stop trying to cheat!" // Red tooltip for locked days
                      : tooltips[day] || `Day ${day}` // Default tooltip
                  }
                >
                  {openedGifts[day] && (
                    <div className="checkmark">
                      ✓
                    </div>
                  )}
                  {day}
                </div>
              ))}
            </div>
          ))}
        </div>
        <div style={{ margin: "24px 0" }}>
          <div style={{ fontWeight: 700, fontSize: "1.1rem" }}>
            Trivia Milestones: {totalCorrect} / 30 correct
          </div>
          <div style={{ marginTop: 8, position: "relative" }}>
            {MILESTONES.map(m => (
              <span
                key={m}
                data-tooltip={
                  milestones.includes(m)
                    ? tooltips[milestoneTooltipKeys[m]] || `Milestone: ${m} correct answers`
                    : undefined
                }
                style={{
                  display: "inline-block",
                  margin: "0 6px",
                  padding: "6px 14px",
                  borderRadius: "12px",
                  background: milestones.includes(m) ? milestoneColors[m] : "#e2e8f0",
                  color: milestones.includes(m) ? "#24406b" : "#888",
                  fontWeight: 700,
                  fontFamily: "'Quicksand', sans-serif",
                  fontSize: "1rem",
                  border: milestones.includes(m) ? "2px solid #24406b" : "none",
                  boxShadow: milestones.includes(m)
                    ? "0 2px 8px rgba(36,62,99,0.10)"
                    : "none",
                  transition: "background 0.2s, color 0.2s, transform 0.2s",
                  cursor: milestones.includes(m) ? "pointer" : "default",
                  position: "relative"
                }}
                onMouseEnter={e => {
                  if (milestones.includes(m)) {
                    e.currentTarget.style.background = milestoneHoverColors[m];
                    e.currentTarget.style.transform = "scale(1.05)";
                  }
                }}
                onMouseLeave={e => {
                  if (milestones.includes(m)) {
                    e.currentTarget.style.background = milestoneColors[m];
                    e.currentTarget.style.transform = "scale(1)";
                  }
                }}
              >
                {m}
              </span>
            ))}
            {milestoneTooltip && (
              <div
                style={{
                  position: "fixed",
                  left: milestoneTooltip.x,
                  top: milestoneTooltip.y,
                  transform: "translate(-50%, -100%)",
                  background: "#24406b",
                  color: "#fff",
                  padding: "7px 18px",
                  borderRadius: 10,
                  fontSize: "1rem",
                  fontFamily: "'Quicksand', sans-serif",
                  fontWeight: 500,
                  pointerEvents: "none",
                  zIndex: 4000,
                  whiteSpace: "nowrap",
                  boxShadow: "0 2px 8px rgba(36,62,99,0.13)"
                }}
              >
                {milestoneTooltip.text}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginBottom: 12 }}>
          <button
            className="shared-button"
            style={{
              ...dayPageButtonStyle,
              fontSize: "0.98rem",
              padding: "8px 20px",
              margin: 0,
            }}
            onMouseEnter={e => (e.currentTarget.style.transform = "scale(1.07)")}
            onMouseLeave={e => (e.currentTarget.style.transform = "scale(1)")}
            onClick={() => setShowHistory(true)}
          >
            View Past Questions
          </button>
        </div>
        {showHistory && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(36,62,99,0.25)",
              zIndex: 3000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "'Quicksand', sans-serif" // Ensure Quicksand everywhere
            }}
            onClick={() => setShowHistory(false)}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: 18,
                boxShadow: "0 8px 32px rgba(36,62,99,0.18)",
                padding: 32,
                maxWidth: 600,
                width: "90vw",
                maxHeight: "80vh",
                overflowY: "auto",
                position: "relative",
                fontFamily: "inherit"
              }}
              onClick={e => e.stopPropagation()}
            >
              <button
                style={{
                  position: "absolute",
                  top: 12,
                  right: 18,
                  background: "#e0eaff", // Slightly darker by default
                  border: "none",
                  fontSize: 28,
                  cursor: "pointer",
                  color: "#7eaaff",
                  fontWeight: 700,
                  borderRadius: "50%",
                  width: 38,
                  height: 38,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background 0.2s, color 0.2s, transform 0.2s",
                  boxShadow: "0 2px 8px rgba(36,62,99,0.10)"
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = "#b3d4fc";
                  e.currentTarget.style.color = "#24406b";
                  e.currentTarget.style.transform = "scale(1.13) rotate(12deg)";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = "#e0eaff";
                  e.currentTarget.style.color = "#7eaaff";
                  e.currentTarget.style.transform = "scale(1) rotate(0deg)";
                }}
                aria-label="Close"
                onClick={() => setShowHistory(false)}
              >
                ×
              </button>
              <h2 style={{ marginBottom: 18, fontFamily: "inherit" }}>
                Past Questions
              </h2>
              <ul style={{ listStyle: "none", padding: 0, fontFamily: "inherit" }}>
                {triviaQuestions
                  .filter(q => triviaAnswers[q.day]?.answered)
                  .map(q => (
                    <li key={q.day} style={{ marginBottom: 18, textAlign: "left", fontFamily: "inherit" }}>
                      <div style={{ fontWeight: 700, marginBottom: 4, fontFamily: "inherit" }}>
                        Day {q.day}:
                      </div>
                      <div style={{ marginLeft: 8, fontFamily: "inherit" }}>
                        {q.question}
                      </div>
                      <div style={{ height: 8 }} /> {/* Extra line/space between question and answer */}
                      <div style={{ marginTop: 2, marginLeft: 0, fontFamily: "inherit" }}>
                        <span style={{ fontWeight: 700, fontFamily: "inherit" }}>Your answer:</span>{" "}
                        <span style={{ fontWeight: 400, fontFamily: "inherit" }}>
                          {q.type === "multiple-choice"
                            ? q.choices?.[triviaAnswers[q.day]?.answer]
                            : triviaAnswers[q.day]?.answer}
                        </span>
                        {" "}
                        {triviaAnswers[q.day]?.correct ? (
                          <span style={{ color: "#38a169", fontWeight: 700, fontFamily: "inherit" }}>✓</span>
                        ) : (
                          <span style={{ color: "#e53e3e", fontWeight: 700, fontFamily: "inherit" }}>✗</span>
                        )}
                      </div>
                      {!triviaAnswers[q.day]?.correct && (
                        <div style={{ fontSize: "0.95em", color: "#888", marginLeft: 8, fontFamily: "inherit" }}>
                          <span style={{ fontWeight: 400, fontFamily: "inherit" }}>Correct answer:</span>{" "}
                          <span style={{ fontWeight: 400, fontFamily: "inherit" }}>
                            {q.type === "multiple-choice"
                              ? q.choices?.[q.correctIndex ?? 0]
                              : q.answerText}
                          </span>
                        </div>
                      )}
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        )}
      </div>
      {/* Bouncing background images */}
      {showBouncingPhotos && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            zIndex: -1,
          }}
        >
          {bouncers.map((b, i) => (
            <img
              key={b.idx + "-" + i}
              src={`/photos/${photoFilenames[b.idx]}`}
              alt=""
              style={{
                position: "absolute",
                left: positions[i]?.x ?? 0,
                top: positions[i]?.y ?? 0,
                width: IMAGE_SIZE,
                height: IMAGE_SIZE,
                borderRadius: 16,
                objectFit: "cover",
                boxShadow: "0 4px 16px rgba(0,0,0,0.13)",
                pointerEvents: "none",
                zIndex: -1,
                opacity: 0.25,
                transition: "none",
                willChange: "transform",
              }}
            />
          ))}
        </div>
      )}
      <img
        src="/logo/logo.png"
        alt="Logo"
        style={{
          position: "fixed",
          top: 12,
          left: 12,
          width: 64,
          height: 64,
          zIndex: 10000,
          pointerEvents: "auto",
          userSelect: "none",
          opacity: 0.7,
          transition: "transform 0.5s cubic-bezier(.4,2,.6,1), opacity 0.2s",
          cursor: "pointer",
        }}
        onMouseEnter={e => {
          (e.currentTarget as HTMLImageElement).style.transform = "rotate(360deg) scale(1.08)";
          (e.currentTarget as HTMLImageElement).style.opacity = "1";
        }}
        onMouseLeave={e => {
          (e.currentTarget as HTMLImageElement).style.transform = "rotate(0deg) scale(1)";
          (e.currentTarget as HTMLImageElement).style.opacity = "0.7";
        }}
        onClick={() => setShowLogoMenu(v => !v)}
      />
      {showLogoTooltip && (
        <div
          style={{
            position: "fixed",
            top: 20,
            left: 84,
            background: "#ffe066", // yellow
            color: "#5a3b00",      // brown text
            padding: "6px 14px",
            borderRadius: 8,
            fontSize: "0.95rem",
            fontFamily: "'Quicksand', sans-serif",
            fontWeight: 500,
            pointerEvents: "none",
            zIndex: 10001,
            boxShadow: "0 2px 8px rgba(36,62,99,0.13)",
            whiteSpace: "nowrap",
            opacity: 0.97,
            transition: "opacity 0.2s"
          }}
        >
          Get 5 questions right!
        </div>
      )}
      {showLogoMenu && (
        <div
          style={{
            position: "fixed",
            top: 80,
            left: 12,
            background: "linear-gradient(135deg, #fff6f6 0%, #ffe9fa 100%)", // pastel gradient
            borderRadius: 18,
            boxShadow: "0 8px 32px rgba(244,182,202,0.18)", // soft, large shadow
            padding: "22px 0 16px 0",
            minWidth: 220,
            zIndex: 10001,
            fontFamily: "'Quicksand', sans-serif",
            display: "flex",
            flexDirection: "column",
            gap: 0,
            border: "2.5px solid #ffd6e0", // soft pink border
            alignItems: "stretch",
            transition: "box-shadow 0.3s, background 0.3s"
          }}
          onMouseLeave={() => setShowLogoMenu(false)}
        >
          <div style={{
            fontWeight: 700,
            fontSize: "1.13rem",
            color: "#d16ba5",
            padding: "0 28px 14px 28px",
            fontFamily: "'Quicksand', sans-serif",
            letterSpacing: "0.01em",
            textAlign: "center"
          }}>
            🌸 Unlocks & Goodies
          </div>
          <button
            style={{
              background: milestones.includes(5) ? "#ffb3b3" : "#f8f8f8",
              color: milestones.includes(5) ? "#24406b" : "#bbb",
              border: "none",
              padding: "13px 28px",
              fontSize: "1.05rem",
              fontWeight: 700,
              cursor: milestones.includes(5) ? "pointer" : "not-allowed",
              width: "calc(100% - 28px)",
              textAlign: "left",
              fontFamily: "'Quicksand', sans-serif",
              borderBottom: "1px solid #ffe5ec",
              borderRadius: "14px",
              margin: "0 14px 10px 14px",
              boxShadow: milestones.includes(5) ? "0 2px 8px #ffb3b340" : "none",
              transition: "background 0.2s, color 0.2s, transform 0.2s"
            }}
            disabled={!milestones.includes(5)}
            onMouseEnter={e => {
              if (milestones.includes(5)) {
                e.currentTarget.style.background = "#ff8fab";
                e.currentTarget.style.transform = "scale(1.05)";
              }
            }}
            onMouseLeave={e => {
              if (milestones.includes(5)) {
                e.currentTarget.style.background = "#ffb3b3";
                e.currentTarget.style.transform = "scale(1)";
              }
            }}
            onClick={() => {
              if (milestones.includes(5)) {
                window.open("https://discord.com/channels/1366471898463993856/1366495684630679673", "_blank");
              }
            }}
          >
            {milestones.includes(5) ? "Hidden Discord" : "Reach 5 correct"}
          </button>
          <button
            style={{
              background: milestones.includes(10) ? "#ffd699" : "#f8f8f8",
              color: milestones.includes(10) ? "#24406b" : "#bbb",
              border: "none",
              padding: "13px 28px",
              fontSize: "1.05rem",
              fontWeight: 700,
              cursor: milestones.includes(10) ? "pointer" : "not-allowed",
              width: "calc(100% - 28px)",
              textAlign: "left",
              fontFamily: "'Quicksand', sans-serif",
              borderBottom: "1px solid #ffe5ec",
              borderRadius: "14px",
              margin: "0 14px 10px 14px",
              boxShadow: milestones.includes(10) ? "0 2px 8px #ffd69940" : "none",
              transition: "background 0.2s, color 0.2s, transform 0.2s"
            }}
            disabled={!milestones.includes(10)}
            onMouseEnter={e => {
              if (milestones.includes(10)) {
                e.currentTarget.style.background = "#ffe066";
                e.currentTarget.style.transform = "scale(1.05)";
              }
            }}
            onMouseLeave={e => {
              if (milestones.includes(10)) {
                e.currentTarget.style.background = "#ffd699";
                e.currentTarget.style.transform = "scale(1)";
              }
            }}
            onClick={() => {
              if (milestones.includes(10)) {
                alert("You can get help on a question!");
              }
            }}
          >
            {milestones.includes(10) ? "+1 Help" : "Reach 10 correct"}
          </button>
          <button
            style={{
              background: milestones.includes(15) ? "#b9fbc0" : "#f8f8f8",
              color: milestones.includes(15) ? "#24406b" : "#bbb",
              border: "none",
              padding: "13px 28px",
              fontSize: "1.05rem",
              fontWeight: 700,
              cursor: milestones.includes(15) ? "pointer" : "not-allowed",
              width: "calc(100% - 28px)",
              textAlign: "left",
              fontFamily: "'Quicksand', sans-serif",
              borderBottom: "1px solid #ffe5ec",
              borderRadius: "14px",
              margin: "0 14px 10px 14px",
              boxShadow: milestones.includes(15) ? "0 2px 8px #b9fbc040" : "none",
              transition: "background 0.2s, color 0.2s, transform 0.2s"
            }}
            disabled={!milestones.includes(15)}
            onMouseEnter={e => {
              if (milestones.includes(15)) {
                e.currentTarget.style.background = "#aef7b6";
                e.currentTarget.style.transform = "scale(1.05)";
              }
            }}
            onMouseLeave={e => {
              if (milestones.includes(15)) {
                e.currentTarget.style.background = "#b9fbc0";
                e.currentTarget.style.transform = "scale(1)";
              }
            }}
            onClick={() => {
              if (milestones.includes(15)) {
                window.open("https://drive.google.com/drive/folders/14nWY9l2myogcmyVZzRAdXsX9JuLDgZdG", "_blank");
              }
            }}
          >
            {milestones.includes(15) ? "Raw footage" : "Reach 15 correct"}
          </button>
          <button
            style={{
              background: milestones.includes(20) ? "#b3e5fc" : "#f8f8f8",
              color: milestones.includes(20) ? "#24406b" : "#bbb",
              border: "none",
              padding: "13px 28px",
              fontSize: "1.05rem",
              fontWeight: 700,
              cursor: milestones.includes(20) ? "pointer" : "not-allowed",
              width: "calc(100% - 28px)",
              textAlign: "left",
              fontFamily: "'Quicksand', sans-serif",
              borderBottom: "1px solid #ffe5ec",
              borderRadius: "14px",
              margin: "0 14px 10px 14px",
              boxShadow: milestones.includes(20) ? "0 2px 8px #b3e5fc40" : "none",
              transition: "background 0.2s, color 0.2s, transform 0.2s"
            }}
            disabled={!milestones.includes(20)}
            onMouseEnter={e => {
              if (milestones.includes(20)) {
                e.currentTarget.style.background = "#a3d8f4";
                e.currentTarget.style.transform = "scale(1.05)";
              }
            }}
            onMouseLeave={e => {
              if (milestones.includes(20)) {
                e.currentTarget.style.background = "#b3e5fc";
                e.currentTarget.style.transform = "scale(1)";
              }
            }}
            onClick={() => {
              if (milestones.includes(20)) {
                alert("You can get help on a question!");
              }
            }}
          >
            {milestones.includes(20) ? "+1 Help" : "Reach 20 correct"}
          </button>
          <button
            style={{
              background: milestones.includes(25) ? "#d1b3ff" : "#f8f8f8",
              color: milestones.includes(25) ? "#24406b" : "#bbb",
              border: "none",
              padding: "13px 28px",
              fontSize: "1.05rem",
              fontWeight: 700,
              cursor: milestones.includes(25) ? "pointer" : "not-allowed",
              width: "calc(100% - 28px)",
              textAlign: "left",
              fontFamily: "'Quicksand', sans-serif",
              borderBottom: "1px solid #ffe5ec",
              borderRadius: "14px",
              margin: "0 14px 10px 14px",
              boxShadow: milestones.includes(25) ? "0 2px 8px #d1b3ff40" : "none",
              transition: "background 0.2s, color 0.2s, transform 0.2s"
            }}
            disabled={!milestones.includes(25)}
            onMouseEnter={e => {
              if (milestones.includes(25)) {
                e.currentTarget.style.background = "#c7aef7";
                e.currentTarget.style.transform = "scale(1.05)";
              }
            }}
            onMouseLeave={e => {
              if (milestones.includes(25)) {
                e.currentTarget.style.background = "#d1b3ff";
                e.currentTarget.style.transform = "scale(1)";
              }
            }}
            onClick={() => {
              if (milestones.includes(25)) {
                window.open("https://github.com/Macomatic/AA-2025", "_blank");
              }
            }}
          >
            {milestones.includes(25) ? "Github Repo" : "Reach 25 correct"}
          </button>
          <button
            style={{
              background: milestones.includes(30) ? "#ffe066" : "#f8f8f8",
              color: milestones.includes(30) ? "#24406b" : "#bbb",
              border: "none",
              padding: "13px 28px",
              fontSize: "1.05rem",
              fontWeight: 700,
              cursor: milestones.includes(30) ? "pointer" : "not-allowed",
              width: "calc(100% - 28px)",
              textAlign: "left",
              fontFamily: "'Quicksand', sans-serif",
              borderBottom: "1px solid #ffe5ec",
              borderRadius: "14px",
              margin: "0 14px 10px 14px",
              boxShadow: milestones.includes(30) ? "0 2px 8px #ffe06640" : "none",
              transition: "background 0.2s, color 0.2s, transform 0.2s"
            }}
            disabled={!milestones.includes(30)}
            onMouseEnter={e => {
              if (milestones.includes(30)) {
                e.currentTarget.style.background = "#ffe8a3";
                e.currentTarget.style.transform = "scale(1.05)";
              }
            }}
            onMouseLeave={e => {
              if (milestones.includes(30)) {
                e.currentTarget.style.background = "#ffe066";
                e.currentTarget.style.transform = "scale(1)";
              }
            }}
            onClick={() => {
              if (milestones.includes(30)) {
                alert("It's finally time...");
              }
            }}
          >
            {milestones.includes(30) ? "Room Tour" : "Reach 30 correct"}
          </button>
        </div>
      )}
    </div>
  );
};

export default MainScreen;